import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Send, MessageSquare, AlertTriangle, ShieldAlert } from "lucide-react";

/**
 * Admin dialog for sending an urgent SMS broadcast. Targets either
 * everyone signed up on a specific Saturday, all board members, or both.
 * Shows a live preview + a per-recipient result table after send.
 */
export default function BroadcastSmsDialog({ open, onClose, upcomingSaturdays = [] }) {
  const [message, setMessage] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [allMembers, setAllMembers] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setMessage("");
      setTargetDate(upcomingSaturdays[0] || "");
      setAllMembers(false);
      setConfirming(false);
      setResult(null);
    }
  }, [open, upcomingSaturdays]);

  const remaining = 280 - message.length;
  const tooLong = remaining < 0;
  const validTarget = !!targetDate || allMembers;

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const payload = { message };
      if (targetDate) payload.date_iso = targetDate;
      if (allMembers) payload.all_members = true;
      const { data } = await api.post("/admin/sms/broadcast", payload);
      setResult(data);
      toast.success(`SMS queued for ${data.targeted} volunteer${data.targeted === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Broadcast failed");
    } finally { setSending(false); setConfirming(false); }
  };

  const preview = useMemo(() => {
    const core = (message || "").trim();
    if (!core) return "";
    return core.toLowerCase().endsWith("stop to opt out.") ? core : `${core} Reply STOP to opt out.`;
  }, [message]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="broadcast-sms-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-amber-100 text-amber-700"><MessageSquare className="w-5 h-5" /></div>
            <DialogTitle className="font-heading text-2xl">Send urgent SMS</DialogTitle>
          </div>
          <DialogDescription>
            Use for last-minute needs: weather cancellation, driver shortage, address change. Only volunteers who opted in receive the message.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-semibold">Target</Label>
              <div className="mt-1.5 space-y-2">
                {upcomingSaturdays.length > 0 && (
                  <select
                    data-testid="broadcast-target-date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-sm focus-ring"
                  >
                    <option value="">— No specific Saturday —</option>
                    {upcomingSaturdays.map((d) => (
                      <option key={d} value={d}>Volunteers on {d}</option>
                    ))}
                  </select>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-700 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <Switch
                    data-testid="broadcast-all-members"
                    checked={allMembers}
                    onCheckedChange={setAllMembers}
                  />
                  <span>Also blast every board member with a phone on file</span>
                </label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="broadcast-message" className="text-sm font-semibold">Message</Label>
                <span className={`text-xs ${tooLong ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{remaining} chars left</span>
              </div>
              <Textarea
                id="broadcast-message"
                data-testid="broadcast-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder='e.g. "Saturday Aug 15 is CANCELLED due to weather. Reschedule Aug 22. Contact Rob with questions."'
                rows={4}
                className="mt-1.5 text-sm"
              />
              {preview && (
                <div className="mt-2 text-xs text-slate-500">
                  <span className="font-semibold text-slate-600">Preview:</span> {preview}
                </div>
              )}
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                SMS is sent instantly and can&apos;t be recalled. Double-check the message and the target before confirming.
              </span>
            </div>
          </div>
        ) : (
          <div data-testid="broadcast-result" className="space-y-3 mt-2 max-h-72 overflow-y-auto">
            <div className="rounded-md bg-brand-green/10 border border-brand-green/30 text-brand-green-dark px-3 py-2 text-sm font-semibold">
              Sent to {result.targeted} recipient{result.targeted === 1 ? "" : "s"}
              {!result.twilio_enabled && <span className="ml-2 text-xs font-normal bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">DRY RUN — TWILIO_ENABLED is off</span>}
            </div>
            <ul className="text-xs divide-y divide-slate-100">
              {result.results.map((r) => (
                <li key={r.phone} className="flex items-center justify-between py-1.5">
                  <span className="text-slate-700"><span className="font-medium">{r.name}</span> · {r.phone}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                    r.status === "queued" ? "bg-brand-blue/15 text-brand-blue-dark" :
                    r.status === "disabled" ? "bg-slate-200 text-slate-700" :
                    r.status === "failed" ? "bg-rose-100 text-rose-700" :
                    "bg-amber-100 text-amber-800"
                  }`}>{r.status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!result ? (
            !confirming ? (
              <>
                <Button variant="outline" onClick={onClose} data-testid="broadcast-cancel">Cancel</Button>
                <Button
                  data-testid="broadcast-review-button"
                  disabled={!message.trim() || tooLong || !validTarget}
                  onClick={() => setConfirming(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Send className="w-4 h-4 mr-1.5" /> Review &amp; send
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setConfirming(false)} disabled={sending}>Back</Button>
                <Button
                  data-testid="broadcast-confirm-button"
                  onClick={send}
                  disabled={sending}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  <AlertTriangle className="w-4 h-4 mr-1.5" />
                  {sending ? "Sending…" : "Yes, send now"}
                </Button>
              </>
            )
          ) : (
            <Button onClick={onClose} data-testid="broadcast-done" className="bg-brand-blue hover:bg-brand-blue-dark">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
